import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import styles from './ManagementProfileTab.module.scss';
import useManagementProfileTabLogic from './useManagementProfileTabLogic';
import useDirectorDrawerUrlFlag from './useDirectorDrawerUrlFlag';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import ManagementUserProfile from '../Overview/components/ManagementProfile/components/ManagementUserProfile';
import { noDataFoundEmptyState, debtProdFilters, PURPLE_CROSS } from '../../../../assets/hostedassets';
import { isAppliedFiltersEmpty, normalizeNameKey } from '../../../../utils/utils';
import { useFocusRowFromUrlParam } from '../../../../utils/customHooks';
import greyLock from '../../../../assets/grey-lock-2.svg';
import { DATE_FORMATS, formatDateByFormat } from '../../../../utils/dateUtils';
import { TabModule } from '../../../../utils/constants/commonConstants';
import McaRefreshLoader, { McaDocData } from '../../../../common/McaRefreshLoader/McaRefreshLoader';
import { DATA_FETCH_STATUS } from '../../../../store/dashboard/constants';
import { BureauType } from '../CreditBureau/components/Report/CreditBureauConstants';
import DataTable from '../../../../common/_custom/DataTable/DataTable';
import { DataTableColumnType } from '../../../../common/_custom/DataTable/DataTable.types';
import { hasFilterableColumns } from '../../../../common/_custom/DataTable/utils';
import { ComponentCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/ComponentCellRenderer/ComponentCellRenderer';
import { NumberCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/NumberCellRenderer/NumberCellRenderer';
import { TextInputCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/TextInputCellRenderer/TextInputCellRenderer';
import { SingleSelectDropdownCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/SingleSelectDropdownCellRenderer';
import { MultiSelectDropdownCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/MultiSelectDropdownCellRenderer/MultiSelectDropdownCellRenderer';
import { DatePickerCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/DatePickerCellRenderer/DatePickerCellRenderer';
import Button from '../../../../common/_custom/Button/Button';
import { setCustomFilterDrawer } from '../../../../store/investor/action';
import HistoryDrawer from '../../../../common/HistoryDrawer/HistoryDrawer';
import {
    DIRECTOR_ROW_DATA_ATTR,
    FOCUS_DIRECTOR_PARAM,
    LOCK_HEADER_ICON_KEY,
    MANAGEMENT_PROFILE_FILTER_SOURCE_KEY,
} from './ManagementProfileTabConstants';
import { isIdentityCellLocked } from '../../../../common/ManagementProfile/managementProfileFields';
import { MANAGEMENT_PROFILE_FIELD_VALIDATORS } from '../../../../common/ManagementProfile/managementProfileFieldValidators';
import { createManagementProfileComponentRegistry, ManagementProfileStatusPillRenderer } from './managementProfileColumnRenderers';
import { managementProfileValueGetterMap } from './managementProfileValueGetters';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_MANAGEMENT_DIRECTOR_VIEWED } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import AddDirectorModal from '../../../../common/AddDirectorModal/AddDirectorModal';
import { plusWhite } from '../../../../assets/hostedassets';
import { useIsMobile } from '../../../../common/_custom/Hooks/useIsMobile';
import MobileDirectorCard from './MobileDirectorCard';
import FetchDetailsBanner from './FetchDetailsBanner';

const DIRECTOR_ROW_DATA_ATTR_KEY = `data-${DIRECTOR_ROW_DATA_ATTR}`;

interface ManagementProfileTabProps {
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
    mcaDocData?: McaDocData | null;
}

function ManagementProfileTab({ navigateToTab, mcaDocData }: ManagementProfileTabProps) {
    const dispatch = useDispatch();
    const userData = useSelector((state: any) => state?.user?.userData);
    const params = useParams();
    // Phones get director cards instead of the DataTable — see the body below. RECUR-27903.
    const isMobile = useIsMobile();
    const {
        overviewLoading,
        directors,
        totalDirectorsCount,
        columnLocks,
        selectedDirector,
        setSelectedDirector,
        lastUpdatedAt,
        columns,
        pinnedColumns,
        updateCustomFieldStatus,
        updateSingleSelectDropdown,
        updateMultiSelectDropdown,
        onHistoryClick,
        historyDrawerState,
        handleHistoryDrawerClose,
        appliedFilters,
        onFiltersApply,
        refetchDirectors,
        saveDirectorFields,
        isLinkedinRefreshing,
        isSelectedDirectorPanEnriching,
        armPanProfileEnrichment,
        isPanProfileEnrichmentPending,
        isDirectorEditingEnabled,
        showFetchBanner,
        handleFetchDetails,
        panProfileNameUpdate,
    } = useManagementProfileTabLogic();

    useDirectorDrawerUrlFlag(!!selectedDirector);

    const [, setSearchParams] = useSearchParams();

    useFocusRowFromUrlParam({
        ready: !overviewLoading && directors.length > 0,
        paramKey: FOCUS_DIRECTOR_PARAM,
        dataAttr: DIRECTOR_ROW_DATA_ATTR,
    });

    // Both roster surfaces carry the same handle, so a focus lands wherever the user is reading.
    const rowDataAttributes = useCallback((row: any) => ({ [DIRECTOR_ROW_DATA_ATTR_KEY]: normalizeNameKey(row?.managementProfileId) }), []);

    const [addDirectorOpen, setAddDirectorOpen] = useState(false);
    // The director this tab just added, held until the roster is final. The enrichment renames and
    // re-sorts the row seconds later, so focusing on save would mark a row about to move.
    const [addedDirectorId, setAddedDirectorId] = useState('');
    // Set only by this modal's own save, so the hold is scoped to the director it added: a modal
    // reopened while an earlier enrichment is still running is neither shown its spinner nor
    // closed under the user mid-typing.
    const [addDirectorAwaitingEnrichment, setAddDirectorAwaitingEnrichment] = useState(false);

    const closeAddDirector = useCallback(() => {
        setAddDirectorAwaitingEnrichment(false);
        setAddDirectorOpen(false);
    }, []);

    // The Add modal is held open while the enrichment runs; close it when the hold clears,
    // whether it landed or the poll gave up. Mirrors the edit modal's close-on-settle.
    useEffect(() => {
        if (addDirectorAwaitingEnrichment && !isPanProfileEnrichmentPending) closeAddDirector();
    }, [addDirectorAwaitingEnrichment, isPanProfileEnrichmentPending, closeAddDirector]);

    // Publishing the param is all this does — useFocusRowFromUrlParam owns the scroll, the
    // highlight and clearing the param again.
    useEffect(() => {
        if (!addedDirectorId || isPanProfileEnrichmentPending) return;
        setAddedDirectorId('');
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.set(FOCUS_DIRECTOR_PARAM, addedDirectorId);
                return next;
            },
            { replace: true },
        );
    }, [addedDirectorId, isPanProfileEnrichmentPending, setSearchParams]);

    // A modal left open across an org switch (e.g. via URL nav while still mounted) must not
    // submit its half-filled form against the new org — close it so a reopen starts fresh.
    useEffect(() => {
        closeAddDirector();
    }, [params?.id, closeAddDirector]);

    // Mixpanel event only ever carries the PNO/company identifiers, never a
    // per-director one (directorId/din/directorName) — CodeRabbit finding on #3232,
    // this dashboard counts per-PNO module usage, not a per-director breakdown.
    const handleDirectorClick = (row: any) => {
        Mixpanel.track(AICA_MANAGEMENT_DIRECTOR_VIEWED, {
            parentId: userData?.investorOrgId,
            companyOrgId: params?.id,
            websiteUrl: window.location.href,
        });
        setSelectedDirector(row);
    };

    // Mobile renders director cards, not the DataTable — and DataTable is what mounts
    // DataTableFilterDrawer. Showing the button there would dispatch customFilterDrawer.open
    // and render no drawer, i.e. a control that visibly does nothing.
    const showFilterButton = useMemo(() => hasFilterableColumns(columns) && !isMobile, [columns, isMobile]);
    const hasActiveFilters = !isAppliedFiltersEmpty(appliedFilters);

    // Desktop's .headerRight always mounts McaRefreshLoader, so an MCA refresh that starts
    // before any timestamp exists is still visible there. Mobile's meta row is conditional,
    // so it has to gate on the SAME condition the loader uses internally — otherwise the
    // refresh status would be invisible on a company that has never been fetched.
    const isMcaRefreshing = mcaDocData?.isFetching === true && mcaDocData?.reportStatus === DATA_FETCH_STATUS.PROCESSING;

    // Filtering is applied by underwriting-engine, so clearing has to go back to the server —
    // `onFiltersApply({})` resets the hook's state AND refetches. The dispatch additionally
    // clears the drawer's own redux copy, so its checkboxes aren't still ticked on reopen.
    const clearFilters = useCallback(() => {
        onFiltersApply({});
        dispatch(setCustomFilterDrawer({ open: false, appliedFilters: { [MANAGEMENT_PROFILE_FILTER_SOURCE_KEY]: {} } }));
    }, [onFiltersApply, dispatch]);

    // `navigateToTab` is a plain function declared in Dashboard's render (index.tsx:433), so it
    // has a new identity every time Dashboard renders. Depending on that identity rebuilt this
    // registry -> customCellRenderers -> useCellRenderers' map on every render, and because that
    // map's VALUES are the cell component types, React unmounted and remounted every <td>
    // rather than updating it. Hovering then self-sustained: the remount destroyed the element
    // under the cursor, the browser fired mouseenter on its replacement, that tooltip set state,
    // repeat — measured at ~55 cell remounts/second. It closed any open dropdown (StatusDropdown
    // lost its isOpen state) and reset the hover-revealed history icon. Hold the callback in a
    // ref and depend on a stable wrapper instead.
    const navigateToTabRef = useRef(navigateToTab);
    navigateToTabRef.current = navigateToTab;
    const stableNavigateToTab = useCallback(
        (tabId: string, additionalParams?: Record<string, string>) => navigateToTabRef.current?.(tabId, additionalParams),
        [],
    );

    const componentRenderFnRegistry = useMemo(
        () => createManagementProfileComponentRegistry({ columnLocks, navigateToTab: stableNavigateToTab }),
        [columnLocks, stableNavigateToTab],
    );

    const customCellRenderers = useMemo(
        () => ({
            [DataTableColumnType.COMPONENT]: (props: any) => (
                <ComponentCellRenderer {...props} dynamicProps={{ componentRenderFnRegistry }} />
            ),
            [DataTableColumnType.PILL]: (props: any) => <ManagementProfileStatusPillRenderer {...props} />,
            [DataTableColumnType.TEXT]: (props: any) => (
                <TextInputCellRenderer
                    {...props}
                    dynamicProps={{
                        updateCustomFieldStatus,
                        isDisabled: !!props?.column?.disable || isIdentityCellLocked(props?.column?.field, props?.row),
                        validate: MANAGEMENT_PROFILE_FIELD_VALIDATORS[props?.column?.field],
                        truncate: true,
                    }}
                />
            ),
            [DataTableColumnType.NUMBER]: (props: any) => (
                <NumberCellRenderer {...props} dynamicProps={{ updateCustomFieldStatus, isDisabled: !!props?.column?.disable }} />
            ),
            [DataTableColumnType.SINGLE_SELECT_DROPDOWN]: (props: any) => (
                <SingleSelectDropdownCellRenderer
                    {...props}
                    dynamicProps={{ updateSingleSelectDropdown, isDisabled: !!props?.column?.disable }}
                />
            ),
            [DataTableColumnType.MULTI_SELECT_DROPDOWN]: (props: any) => (
                <MultiSelectDropdownCellRenderer
                    {...props}
                    dynamicProps={{ updateMultiSelectDropdown, isDisabled: !!props?.column?.disable }}
                />
            ),
            [DataTableColumnType.DATE_PICKER]: (props: any) => (
                <DatePickerCellRenderer {...props} dynamicProps={{ updateCustomFieldStatus, isDisabled: !!props?.column?.disable }} />
            ),
        }),
        [componentRenderFnRegistry, updateCustomFieldStatus, updateSingleSelectDropdown, updateMultiSelectDropdown],
    );

    /*
        The Source badge + Filters + Add group. On desktop it renders inside .headerRight
        beside the last-updated line, exactly as before. On mobile it renders inside
        .headerLeft instead, so it rides with the sticky title row rather than being pinned
        to the viewport separately — otherwise the title scrolls away and the badge does not.
    */
    const headerActions = (
        <div className={styles.HeaderActions}>
            <div className={styles.dataSourceBadge}>
                <span className={styles.dataSourceBadgeLabel}>Source:</span>
                <span className={styles.dataSourceBadgeValue}>MCA</span>
            </div>
            {/* Columns are config-driven, so a PNO whose ViewConfig seeds no filterable
                        column (and the fallback columns, which have none) would otherwise open an
                        empty drawer. */}
            {showFilterButton && (
                <Button
                    text=""
                    icon={debtProdFilters}
                    variant="white"
                    aria-label="Filters"
                    onClick={() => dispatch(setCustomFilterDrawer({ open: true }))}
                    className={`${styles.filterButton} ${hasActiveFilters ? styles.filterButtonActive : ''}`}
                    // Same active-filter affordance as the Deals and Leads tables: the
                    // button goes purple and grows a cross that clears in one click.
                    endIcon={hasActiveFilters ? PURPLE_CROSS : null}
                    endIconStyles={{ height: '0.8rem', marginLeft: '0.25rem' }}
                    endIconClick={clearFilters}
                />
            )}
            <Button
                text="Add"
                icon={plusWhite}
                variant="primary"
                aria-label="Add Director"
                onClick={() => setAddDirectorOpen(true)}
                className={styles.filterButton}
            />
        </div>
    );

    return (
        <div className={styles.ManagementProfileTab}>
            {/*
                Announces the PAN enrichment renaming a roster row, on both the desktop name cell
                and the mobile card title. Always mounted and empty: a region created by the same
                commit that fills it is not present for the change, so it is never spoken. It lives
                at the tab level, not per row, because DataTable folds the row index into its row
                and cell keys — a rename re-sorts the roster, so a per-row region would be a
                different DOM node by the time the name it announces arrives.
            */}
            <div className={styles.SrOnly} aria-live="polite">
                {panProfileNameUpdate}
            </div>
            {isMobile ? (
                /*
                    Mobile emits the two rows as DIRECT children of .ManagementProfileTab —
                    no .header wrapper. A sticky element can only travel inside its parent's
                    box, so while .headerLeft sat inside the short .header it stopped pinning
                    almost immediately. As a direct child its containing block is the whole
                    tab, so the title row stays put for the entire scroll while the
                    last-updated row below it scrolls away.
                */
                <>
                    <div className={styles.MobileTitleRow}>
                        <h2 className={styles.heading}>Management Profile</h2>
                        {headerActions}
                    </div>
                    {(lastUpdatedAt || isMcaRefreshing) && (
                        <div className={styles.MobileMetaRow}>
                            <McaRefreshLoader mcaDocData={mcaDocData} />
                            {lastUpdatedAt && (
                                <span className={styles.lastUpdated}>
                                    Last updated on {formatDateByFormat(lastUpdatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}
                                </span>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <h2 className={styles.heading}>Management Profile</h2>
                    </div>
                    <div className={styles.headerRight}>
                        <McaRefreshLoader mcaDocData={mcaDocData} />
                        {lastUpdatedAt && (
                            <span className={styles.lastUpdated}>
                                Last updated on {formatDateByFormat(lastUpdatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}
                            </span>
                        )}
                        {headerActions}
                    </div>
                </div>
            )}
            {showFetchBanner && <FetchDetailsBanner onFetchDetails={handleFetchDetails} />}
            <div className={styles.body}>
                <div className={styles.content}>
                    {isMobile ? (
                        // A horizontally-scrolling DataTable is unusable on a phone, so the same
                        // `directors` rows render as cards instead — same data, same click-through
                        // to the director drawer. Figma node 3744-84138. RECUR-27903.
                        <div className={styles.MobileCards}>
                            {overviewLoading && <div className={styles.emptyStateTitle}>Loading…</div>}
                            {!overviewLoading && !directors.length && (
                                <div className={styles.emptyState}>
                                    <img src={noDataFoundEmptyState} alt="" className={styles.emptyStateIcon} />
                                    <div className={styles.emptyStateTitle}>No data available</div>
                                </div>
                            )}
                            {!overviewLoading &&
                                directors.map((row) => (
                                    <MobileDirectorCard
                                        key={row.managementProfileId ?? `${row.din}-${row.name}`}
                                        row={row}
                                        onClick={handleDirectorClick}
                                        dataAttributes={rowDataAttributes(row)}
                                    />
                                ))}
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            rows={directors}
                            totalRowCount={totalDirectorsCount}
                            loading={overviewLoading}
                            emptyState={
                                <div className={styles.emptyState}>
                                    <img src={noDataFoundEmptyState} alt="" className={styles.emptyStateIcon} />
                                    <div className={styles.emptyStateTitle}>No data available</div>
                                </div>
                            }
                            noSearchResults={<div className={styles.emptyStateTitle}>No results found</div>}
                            customCellRenderers={customCellRenderers}
                            getRowDataAttributes={rowDataAttributes}
                            valueGetterMap={managementProfileValueGetterMap}
                            pinnedColumns={pinnedColumns}
                            onRowClick={(_e, row) => handleDirectorClick(row)}
                            onHistoryClick={onHistoryClick}
                            headerIconMap={{ [LOCK_HEADER_ICON_KEY]: <img src={greyLock} alt="locked" /> }}
                            filterSourceKey={MANAGEMENT_PROFILE_FILTER_SOURCE_KEY}
                            onFiltersApply={onFiltersApply}
                        />
                    )}
                </div>
            </div>
            {/* Same drawer and same ManagementUserProfile content as desktop — only the width
                changes, since a 60% side panel on a phone is unreadable. Full-width makes it
                read as the full-screen Director Details in the design. RECUR-27903. */}
            {selectedDirector && (
                <SideDrawer onClose={() => setSelectedDirector(null)} width={isMobile ? '100%' : '60%'}>
                    <ManagementUserProfile
                        showUserProfile={selectedDirector}
                        dataPreloaded
                        editable={isDirectorEditingEnabled}
                        linkedinRefreshing={isLinkedinRefreshing}
                        panProfileFetching={isSelectedDirectorPanEnriching}
                        onEditSave={(updates) => saveDirectorFields(selectedDirector.managementProfileId ?? '', updates)}
                        redirectToCreditBureau={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.CREDIT_BUREAU, {
                                bureauType: BureauType.ALL,
                                directorName: director?.name ?? '',
                                focusDirectorId: director?.directorId ?? '',
                            });
                        }}
                        redirectToCompliances={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: director?.name ?? '',
                                directorDin: director?.din ?? '',
                            });
                        }}
                        regulatoryChecksLocked={columnLocks?.regulatoryChecks}
                        redirectToRegulatoryChecks={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: director?.name ?? '',
                                directorDin: director?.din ?? '',
                                section: 'regulatoryChecks',
                            });
                        }}
                        redirectToRiskIndicators={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.RELATED_PARTY, {
                                directorName: director?.name ?? '',
                                directorDin: director?.din ?? '',
                            });
                        }}
                    />
                </SideDrawer>
            )}
            <HistoryDrawer
                open={historyDrawerState.open}
                onClose={handleHistoryDrawerClose}
                referenceId={historyDrawerState.referenceId}
                fieldKey={historyDrawerState.fieldKey}
                title={historyDrawerState.title}
                assesseeOrgId={historyDrawerState.assesseeOrgId}
                historySource={historyDrawerState.historySource}
            />
            <AddDirectorModal
                open={addDirectorOpen}
                onClose={closeAddDirector}
                panProfileFetching={addDirectorAwaitingEnrichment}
                onSuccess={({ managementProfileId, pan }) => {
                    refetchDirectors();
                    // Held open while the enrichment runs, like the edit modal: the save is what
                    // starts it, so the save is where the user should see it happening. Keyed off
                    // what actually armed — a create that carries no id, or a PAN the backend
                    // enriches nothing for, starts no poll and so must close now instead of
                    // waiting on a hold that will never clear.
                    const armed = armPanProfileEnrichment(managementProfileId, pan);
                    setAddDirectorAwaitingEnrichment(armed);
                    setAddedDirectorId(managementProfileId ?? '');
                    if (!armed) setAddDirectorOpen(false);
                }}
                organizationId={params?.id}
            />
        </div>
    );
}

export default ManagementProfileTab;
